// SEM-6250 — the DIAGRAM renderer: boxes on a plane, joined by elbow connectors.
//
// Sibling of OrgChart.tsx, not a replacement for it. The list answers "who
// reports to whom, scan it fast, 50k rows stay smooth"; this answers "show me
// the shape of the company". Both read the same solid-line tree (ADR-012) and
// both are shell-agnostic + label-driven, so the surrounding Content owns the
// i18n and the fetch exactly as it does for the list.
//
// TWO LAYERS, on purpose. The connectors are one SVG; the nodes are absolutely
// positioned HTML. Drawing the boxes in SVG too would have been tidier on paper
// and worse in practice — text truncation, the design tokens, hover states and
// focus rings are all things the HTML/CSS layer already does correctly, and
// re-implementing them inside <text> is how a chart ends up with its own
// accidental typography.
//
// BOUNDED BY DEFAULT. Opens to DEFAULT_DEPTH levels and says on each collapsed
// node how many people it is folding away. An unbounded diagram of a 1000-person
// tenant is neither readable nor fast, and one that silently omits the tail is
// worse than one that admits it.
//
// SCROLLS TO THE ROOT, AND ZOOMS. The 100-person demo tenant lays out 15,500px
// wide at depth 3 — fifteen viewports. Left at scrollLeft 0 the diagram opens on
// whichever mid-level subtree happens to sit at the far left, and the one box
// that orients the reader is 7,000px away. Every unit test passed while this was
// true, because the initial scroll position is not part of what layoutDiagram
// returns. So: centre on the root when a dataset arrives, and give the reader a
// scale control — at 1:1 no org of this size fits any screen, and "show me the
// whole shape" is the question a diagram exists to answer.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { PersonLink } from "@/components/person-link";

import type { OrgChartNode } from "./api";
import { defaultExpanded, layoutDiagram } from "./org-chart-layout";
import { indexTree } from "./org-tree-flatten";
import {
  ORG_VIEW_KEYS,
  readViewState,
  reviveIdList,
  reviveNumber,
  reviveScrollOffset,
  writeViewState,
} from "./org-view-state";

const NODE_WIDTH = 184;
const NODE_HEIGHT = 68;
const H_GAP = 20;
const V_GAP = 48;
/** Padding around the drawn diagram so edge boxes aren't flush to the border. */
const CANVAS_PADDING = 24;
/**
 * Depths whose children are drawn on open — 0 and 1, so THREE tiers are visible
 * (root, its reports, theirs). Measured on the 100-person demo tenant: three
 * tiers is 37 boxes over 5,740px, four tiers is 100 boxes over 15,532px. Four
 * tiers is fifteen viewports of panning to see a tree the reader has no map of;
 * three still shows real structure and is what the depth note promises.
 */
const DEFAULT_DEPTH = 2;

// Decorative glyphs — locale-neutral symbols, never translatable words (the
// accessible name rides on aria-label). Same rule as OrgChart's carets. Shared
// by the expand/collapse toggle and the zoom controls: the symbol is the same
// mark, and giving it two names would only invite the two copies to diverge.
const GLYPH_MINUS = "−";
const GLYPH_PLUS = "+";

const SCALE_MIN = 0.05;
const SCALE_MAX = 1.5;
const SCALE_STEP = 0.15;

/**
 * Below this scale the diagram stops trying to be readable and becomes a map.
 *
 * Measured on the live 100-person demo: fitting the full depth-3 width into a
 * 1280px viewport asks for 0.178, which draws a 184×68 box at 33×12px and its
 * 14px name at 2.5px. That is not a small diagram, it is a grey smear — and
 * since Fit is the most obvious control on the screen, the reader's first click
 * produced something that looked broken.
 *
 * This number was briefly a FLOOR that Fit clamped to (owner decision pending).
 * Clamping is the wrong shape of fix: it makes Fit quietly not fit, so "show me
 * the whole company" still ends in panning. The owner chose the overview mode
 * instead — at 0.45 a 14px name is ~6px and already at the edge of legible, so
 * under it the labels come off and every box becomes a mark. The geometry is
 * untouched; only the paint changes, so the shape the reader wanted is exactly
 * what survives.
 *
 * Derived from scale alone, never from HOW the scale was reached: the same
 * physics applies whether the reader pressed Fit or held down −.
 */
const OVERVIEW_SCALE_THRESHOLD = 0.45;

const clampScale = (value: number): number =>
  Math.min(SCALE_MAX, Math.max(SCALE_MIN, value));

export interface OrgDiagramLabels {
  readonly regionLabel: string;
  readonly expandLabel: string;
  readonly collapseLabel: string;
  readonly noTitle: string;
  readonly empty: string;
  /** e.g. "{{count}} kişi gizli" — shown on a collapsed node. */
  readonly hiddenLabel: (count: number) => string;
  readonly zoomInLabel: string;
  readonly zoomOutLabel: string;
  readonly fitLabel: string;
  /** Shown while labels are dropped, e.g. "Overview — zoom in to read names". */
  readonly overviewLabel: string;
}

export interface OrgDiagramProps {
  readonly nodes: readonly OrgChartNode[];
  readonly labels: OrgDiagramLabels;
  /** Viewport height (px) of the scrollable canvas. */
  readonly height?: number;
}

export function OrgDiagram({
  nodes,
  labels,
  height = 620,
}: OrgDiagramProps): React.ReactElement {
  const { childrenOf, roots } = useMemo(() => indexTree(nodes), [nodes]);

  // What an earlier visit left open outranks the default depth: a reader coming
  // back from a person must not have to re-open the branch they left from
  // (SEM-6349).
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => {
    const stored = readViewState(ORG_VIEW_KEYS.diagramExpanded, reviveIdList);
    return stored === null
      ? defaultExpanded(roots, childrenOf, DEFAULT_DEPTH)
      : new Set(stored);
  });

  useEffect(() => {
    writeViewState(ORG_VIEW_KEYS.diagramExpanded, [...expanded]);
  }, [expanded]);

  const layout = useMemo(
    () =>
      layoutDiagram(roots, childrenOf, expanded, {
        nodeWidth: NODE_WIDTH,
        nodeHeight: NODE_HEIGHT,
        hGap: H_GAP,
        vGap: V_GAP,
      }),
    [roots, childrenOf, expanded],
  );

  const toggle = useCallback((employeeId: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  }, []);

  const [scale, setScale] = useState(() => {
    const stored = readViewState(ORG_VIEW_KEYS.diagramScale, reviveNumber);
    return stored === null ? 1 : clampScale(stored);
  });
  const viewportRef = useRef<HTMLDivElement>(null);
  // What the scroll was last anchored TO, not merely "has it run once": under
  // StrictMode the layout effect runs setup → cleanup → setup on mount while
  // refs survive, so a boolean would report "already restored" on the second
  // pass and fall through to the root centring — undoing the restore in every
  // dev build while the tests (which do not double-invoke) stayed green.
  const anchoredTo = useRef<{ nodes: readonly OrgChartNode[]; scale: number } | null>(null);

  useEffect(() => {
    writeViewState(ORG_VIEW_KEYS.diagramScale, scale);
  }, [scale]);

  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>): void => {
    writeViewState(ORG_VIEW_KEYS.diagramScroll, {
      left: e.currentTarget.scrollLeft,
      top: e.currentTarget.scrollTop,
    });
  }, []);

  // Depends on `nodes` and `scale`, NOT on `expanded`: a new dataset or a zoom
  // step should re-anchor the reader on the root, but expanding a box must not
  // yank the view away from the box they just clicked.
  //
  // The FIRST run is the exception: a reader returning from a person is not
  // arriving at a new dataset, so their own scroll position wins over the root
  // centring they never asked for a second time.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const previous = anchoredTo.current;
    anchoredTo.current = { nodes, scale };
    // Same dataset at the same scale: nothing the reader can see has moved, so
    // leave their scroll where it is.
    if (previous !== null && previous.nodes === nodes && previous.scale === scale) return;
    if (previous === null) {
      const saved = readViewState(ORG_VIEW_KEYS.diagramScroll, reviveScrollOffset);
      if (saved !== null) {
        viewport.scrollLeft = saved.left;
        viewport.scrollTop = saved.top;
        return;
      }
    }
    const root = layout.boxes.find((box) => box.depth === 0);
    if (!root) return;
    const rootCentre = (root.x + NODE_WIDTH / 2 + CANVAS_PADDING) * scale;
    viewport.scrollLeft = rootCentre - viewport.clientWidth / 2;
  }, [nodes, scale]);

  const fit = useCallback((): void => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    // Fit means fit. A tree too wide to show its labels drops them (see
    // OVERVIEW_SCALE_THRESHOLD) rather than stopping short of the viewport,
    // because a Fit that leaves the reader panning has not answered the
    // question the button asks.
    setScale(clampScale(viewport.clientWidth / (layout.width + CANVAS_PADDING * 2)));
  }, [layout.width]);

  // Labels are dropped, not the nodes: the text stays in the DOM as sr-only so
  // the accessibility tree is identical in both modes. A screen-reader user has
  // no "too small to read" problem and must not inherit a sighted one.
  const overview = scale < OVERVIEW_SCALE_THRESHOLD;

  if (nodes.length === 0) {
    return (
      <p
        data-slot="org-diagram-empty"
        className="text-sm text-[var(--fg-muted)]"
      >
        {labels.empty}
      </p>
    );
  }

  const canvasWidth = layout.width + CANVAS_PADDING * 2;
  const canvasHeight = layout.height + CANVAS_PADDING * 2;

  // Hugs the drawing when it is shorter than the viewport. Zoomed out to an
  // overview the canvas is a 40px strip, and a fixed 620px box would frame it
  // with 580px of nothing — which reads as a broken render rather than as a
  // deliberate bird's-eye view.
  const viewportHeight = Math.min(height, canvasHeight * scale);

  const zoomButtonClass =
    "flex h-7 items-center rounded-[8px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-2 text-xs text-[var(--fg-muted)] hover:border-[var(--accent)] hover:text-[var(--accent)]";

  return (
    <div className="relative">
      {/* Without this the missing labels are indistinguishable from a failed
          render — which is precisely the complaint that started this work.
          Naming the mode turns a suspicious screen into an intentional one. */}
      {overview && (
        <p
          data-slot="org-diagram-overview-note"
          className="absolute left-3 top-3 z-10 rounded-[8px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-2 py-1 font-sans text-xs text-[var(--fg-muted)]"
        >
          {labels.overviewLabel}
        </p>
      )}

      <div className="absolute right-3 top-3 z-10 flex items-center gap-1">
        <button type="button" onClick={fit} className={zoomButtonClass}>
          {labels.fitLabel}
        </button>
        <button
          type="button"
          onClick={(): void => setScale((s) => clampScale(s - SCALE_STEP))}
          aria-label={labels.zoomOutLabel}
          className={zoomButtonClass}
        >
          <span aria-hidden="true">{GLYPH_MINUS}</span>
        </button>
        <button
          type="button"
          onClick={(): void => setScale((s) => clampScale(s + SCALE_STEP))}
          aria-label={labels.zoomInLabel}
          className={zoomButtonClass}
        >
          <span aria-hidden="true">{GLYPH_PLUS}</span>
        </button>
      </div>

      <div
        ref={viewportRef}
        role="tree"
        aria-label={labels.regionLabel}
        data-slot="org-diagram"
        data-node-count={layout.boxes.length}
        onScroll={onScroll}
        style={{ height: viewportHeight, overflow: "auto" }}
        className="rounded-[12px] border border-[var(--border-subtle)] bg-[var(--bg-raised)]"
      >
        {/* The scaled canvas keeps its LAYOUT size in the outer box so the
            scrollbars describe the scaled drawing; the inner box is transformed
            from its top-left so scroll maths stays a plain multiply by `scale`. */}
        <div
          style={{ width: canvasWidth * scale, height: canvasHeight * scale }}
        >
          <div
            style={{
              position: "relative",
              width: canvasWidth,
              height: canvasHeight,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
            }}
          >
            <svg
              aria-hidden="true"
              width={canvasWidth}
              height={canvasHeight}
              style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
            >
              {layout.edges.map((edge) => {
                // Elbow: straight down out of the parent, across at the midpoint of
                // the vertical gap, then straight down into the child. The turn sits
                // at the halfway line so every connector in a generation shares it
                // and the row reads as one bus rather than a fan.
                const turnY = edge.fromY + V_GAP / 2 + CANVAS_PADDING;
                const fromX = edge.fromX + CANVAS_PADDING;
                const toX = edge.toX + CANVAS_PADDING;
                return (
                  <path
                    key={`${edge.parentId}->${edge.childId}`}
                    d={`M ${fromX} ${edge.fromY + CANVAS_PADDING} V ${turnY} H ${toX} V ${edge.toY + CANVAS_PADDING}`}
                    fill="none"
                    stroke="var(--border-strong)"
                    strokeWidth={1}
                  />
                );
              })}
            </svg>

            {layout.boxes.map((box) => (
              <div
                key={box.node.employeeId}
                role="treeitem"
                aria-level={box.depth + 1}
                aria-selected={false}
                aria-expanded={box.hasChildren ? box.expanded : undefined}
                data-slot="org-diagram-node"
                style={{
                  position: "absolute",
                  left: box.x + CANVAS_PADDING,
                  top: box.y + CANVAS_PADDING,
                  width: NODE_WIDTH,
                  height: NODE_HEIGHT,
                  // In overview the box IS the mark, filled by the same rule the
                  // dot uses at full size: accent = has reports, neutral = leaf.
                  // That is what makes the zoomed-out picture worth looking at —
                  // the branching stays visible when the words are gone.
                  ...(overview
                    ? {
                        background: box.hasChildren
                          ? "var(--accent)"
                          : "var(--border-default)",
                      }
                    : {}),
                }}
                className={
                  overview
                    ? "rounded-[10px]"
                    : "flex flex-col justify-center gap-0.5 rounded-[10px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-3 py-2 shadow-[var(--shadow-sm)]"
                }
              >
                <div className={overview ? "h-full w-full" : "flex min-w-0 items-center gap-2"}>
                  {!overview && (
                    <span
                      aria-hidden="true"
                      className="h-[9px] w-[9px] shrink-0 rounded-[3px]"
                      style={{
                        background: box.hasChildren
                          ? "var(--accent)"
                          : "var(--border-default)",
                      }}
                    />
                  )}
                  {/* An `<a href>`, not a click handler: right-click → new tab
                      lets a reader inspect one person without giving up the
                      diagram, and the address comes from the one person-link
                      producer (SEM-6329). In overview the link becomes the whole
                      mark, so an unlabelled box still lands on its person. */}
                  <PersonLink
                    employeeId={box.node.employeeId}
                    className={
                      overview
                        ? "block h-full w-full"
                        : "min-w-0 flex-1 truncate text-left font-serif text-[14px] text-[var(--fg-primary)]"
                    }
                  >
                    <span className={overview ? "sr-only" : undefined}>
                      {box.node.displayName}
                    </span>
                  </PersonLink>
                </div>
                <span
                  className={
                    overview
                      ? "sr-only"
                      : "truncate pl-[17px] font-sans text-xs text-[var(--fg-muted)]"
                  }
                >
                  {box.node.jobTitle ?? labels.noTitle}
                </span>

                {box.hasChildren && (
                  <button
                    type="button"
                    onClick={(): void => toggle(box.node.employeeId)}
                    aria-label={
                      box.expanded ? labels.collapseLabel : labels.expandLabel
                    }
                    data-slot="org-diagram-toggle"
                    style={
                      overview
                        ? undefined
                        : {
                            position: "absolute",
                            left: "50%",
                            bottom: -11,
                            translate: "-50% 0",
                          }
                    }
                    // sr-only rather than unrendered: at this scale the 22px
                    // chip is 4px of visual noise and unclickable, but removing
                    // it would take expand/collapse away from keyboard and
                    // screen-reader users, who have no legibility problem to
                    // begin with.
                    className={
                      overview
                        ? "sr-only"
                        : "flex h-[22px] items-center gap-1 rounded-full border border-[var(--border-subtle)] bg-[var(--bg-raised)] px-2 text-[11px] text-[var(--fg-muted)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
                    }
                  >
                    <span aria-hidden="true">
                      {box.expanded ? GLYPH_MINUS : GLYPH_PLUS}
                    </span>
                    {/* The hidden-count is the honest part of a bounded diagram:
                    without it a folded 400-person division is indistinguishable
                    from a leaf. Only rendered when something IS hidden. */}
                    {box.hiddenDescendants > 0 && (
                      <span data-slot="org-diagram-hidden">
                        {labels.hiddenLabel(box.hiddenDescendants)}
                      </span>
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
